import { Link, useNavigate } from "react-router-dom";
import { useApi } from "@/hooks/useApi";
import type { FreePost } from "@/lib/types";
import { Skeleton } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";
import Avatar from "@/components/base/Avatar";
import { formatRelative } from "@/pages/free/components/FreeCard";
import { mediaUrl } from "@/lib/media";
import { useAuth } from "@/components/feature/AuthProvider";

// 메인 자유글 섹션 (최신 3건)
export default function FreeSection() {
  const navigate = useNavigate();
  const { requireLogin } = useAuth();
  const { data, loading, error, reload } = useApi<FreePost[]>("frees.php?size=3");
  const posts = data ?? [];

  const handleWrite = () => {
    if (!requireLogin("/free/write")) return;
    navigate("/free/write");
  };

  return (
    <section className="w-full px-4 md:px-6 py-14 md:py-16">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex items-end justify-between gap-4">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              자유글
            </h2>
            <p className="mt-2 text-foreground-600">회원들의 일상과 생각을 나누는 공간입니다.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleWrite}
              className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-quill-pen-line" aria-hidden="true"></i>
              글쓰기
            </button>
            <Link
              to="/free"
              className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-primary-200 bg-primary-50 px-5 py-2.5 text-sm font-semibold text-primary-700 hover:bg-primary-100 transition-colors"
            >
              전체보기
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="rounded-xl border border-background-200 bg-background-50 p-5">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="space-y-1.5">
                    <Skeleton className="h-3.5 w-24" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                </div>
                <Skeleton className="mt-4 h-4 w-2/3" />
                <Skeleton className="mt-2 h-3 w-full" />
              </div>
            ))}
          </div>
        ) : error ? (
          <EmptyState
            icon="ri-error-warning-line"
            title="일시적으로 불러오지 못했습니다"
            description={error}
            action={
              <button
                type="button"
                onClick={reload}
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                다시 시도
              </button>
            }
          />
        ) : posts.length === 0 ? (
          <EmptyState
            icon="ri-chat-smile-3-line"
            title="아직 등록된 자유글이 없습니다"
            action={
              <button
                type="button"
                onClick={handleWrite}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-quill-pen-line" aria-hidden="true"></i>
                첫 글 남기기
              </button>
            }
          />
        ) : (
          <div className="space-y-4">
            {posts.map((post) => {
              const w = post.writer;
              const writerName =
                [w?.aho, w?.name].filter(Boolean).join(" ") ||
                [post.aho, post.name].filter(Boolean).join(" ") ||
                "회원";
              const photo = w?.photo || post.photo;
              const bizName = w?.biz_name || post.biz_name;
              const firstImage = post.images?.[0];
              const liked = post.is_liked === 1;
              return (
                <article
                  key={post.no}
                  className="rounded-xl border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-200 md:p-5"
                >
                  <Link to={`/free/${post.no}`} className="group block">
                    <div className="flex items-center gap-3">
                      <Avatar name={writerName} photo={photo} size={40} />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-foreground-800">
                          {writerName}
                        </p>
                        <p className="text-xs text-foreground-400">
                          {bizName ? `${bizName} · ` : ""}
                          {formatRelative(post.regdate || post.reg_date)}
                        </p>
                      </div>
                    </div>

                    {post.subject && (
                      <h3 className="mt-3 line-clamp-1 font-heading text-base font-bold text-foreground-950 group-hover:text-primary-700">
                        {post.subject}
                      </h3>
                    )}
                    <p className="mt-1.5 line-clamp-3 text-sm leading-relaxed text-foreground-600">
                      {post.content}
                    </p>

                    {firstImage && (
                      <div className="mt-3 h-48 w-full overflow-hidden rounded-lg bg-background-100 sm:h-60">
                        <img
                          src={mediaUrl(firstImage)}
                          alt={post.subject || "첨부 이미지"}
                          className="h-full w-full object-cover object-top"
                        />
                      </div>
                    )}
                  </Link>

                  <div className="mt-3 flex items-center gap-5 border-t border-background-200 pt-3 text-sm text-foreground-500">
                    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                      <i
                        className={liked ? "ri-heart-3-fill text-accent-600" : "ri-heart-3-line"}
                        aria-hidden="true"
                      ></i>
                      {post.like_cnt ?? 0}
                    </span>
                    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                      <i className="ri-chat-3-line" aria-hidden="true"></i>
                      {post.cmt_cnt ?? 0}
                    </span>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}